import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  People as PeopleIcon,
  Analytics as AnalyticsIcon,
  Security as SecurityIcon,
  Verified as VerifiedIcon,
  Public as PublicIcon
} from '@mui/icons-material';

const Method = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Our Method
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '800px', mx: 'auto' }}>
          How we identify patterns of misconduct and work toward systemic change
        </Typography>
      </Box>

      {/* Main Method Description */}
      <Card sx={{ mb: 6 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Our Comprehensive Approach
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
            The Reckoning Project gathers data for all proven incidents of misconduct by police, prosecutors, 
            judges, experts, or other official actors from sources such as public records, watchdog sites, 
            legal databases, and media outlets. We are requesting information from currently and formerly 
            incarcerated people who have experienced misconduct in their cases.
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
            Applying crowd-sourcing technology and data science allows us to identify actors who committed 
            misconduct and move from isolated accounts to a more sustained, systematic, and comprehensive 
            analysis of all relevant patterns. Ultimately, our goal is to eliminate these illegal practices 
            and incentivize protections against it, while also helping people whose convictions and prison 
            sentences were perhaps caused by official misconduct.
          </Typography>
        </CardContent>
      </Card>

      {/* Three Pillars */}
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 4 }}>
        Three Core Areas of Focus
      </Typography>

      <Grid container spacing={4} sx={{ mb: 6 }}>
        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
            <CardContent sx={{ p: 4, flexGrow: 1 }}>
              <SearchIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Mapping Misconduct Across the System
              </Typography>
              <Typography color="text.secondary">
                We track and code all proven incidents of misconduct by officials, using data from 
                official oversight bodies, established databases and media archives and more.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
            <CardContent sx={{ p: 4, flexGrow: 1 }}>
              <PeopleIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Uncovering Systemic Misconduct
              </Typography>
              <Typography color="text.secondary">
                We gather and work to verify reports from incarcerated individuals alleging misconduct, 
                transforming isolated stories into comprehensive analysis.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', textAlign: 'center' }}>
            <CardContent sx={{ p: 4 }}>
              <AnalyticsIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Exposing Patterns: Tracking Repeat Offenders
              </Typography>
              <Typography color="text.secondary">
                We maintain a comprehensive database to estimate misconduct's true extent and identify 
                involved actors, connecting dots to reveal patterns.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Our Process */}
      <Card sx={{ mb: 6 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Our Research Process
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <PublicIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Public Records Analysis"
                secondary="We systematically review court documents, legal filings, and official reports to identify documented cases of misconduct."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <PeopleIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Community Input"
                secondary="We collect detailed accounts from those who have experienced misconduct, providing a platform for voices that often go unheard."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <VerifiedIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Verification Process"
                secondary="All submitted cases undergo rigorous verification using external sources like news articles, legal documents, and public records."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <AnalyticsIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Data Analysis"
                secondary="We use data science techniques to identify patterns, repeat offenders, and systemic issues across jurisdictions."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <SecurityIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Privacy Protection"
                secondary="We maintain strict confidentiality protocols and only publish verified information with appropriate safeguards."
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Geographic Scope */}
      <Card sx={{ mb: 6 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Geographic Scope
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
            Piloted in Pennsylvania, The Reckoning Project is expanding requests for information about 
            misconduct to other states, and we will gladly accept case information from any geographic 
            location as we gradually expand across the United States.
          </Typography>
        </CardContent>
      </Card>

      {/* Goals and Impact */}
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Our Goals
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                Immediate Goals
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="Create transparency around patterns of misconduct" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Identify repeat offenders across jurisdictions" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Provide data for policy reform efforts" />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                Long-term Impact
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="Incentivize appropriate behavior among legal actors" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Support those affected in achieving greater justice" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Contribute to systemic criminal justice reform" />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Method;
